import { createReducer, on } from '@ngrx/store';
import {
  ClearLoading,
  ClearState,
  FetchAppDataFailure,
  FetchAppDataRequest,
  FetchAppDataSuccess,
  FetchSearchDetailsFailure,
  FetchSearchDetailsRequest,
  FetchSearchDetailsSuccess,
  FetchTranslationsFailure,
  FetchTranslationsRequest,
  FetchTranslationsSuccess,
  StartLoading,
  StopLoading,
} from './shared.actions';

// State for this feature (User)
export interface SharedState {
  loading: number[];
  appDataDetails: any;
  abilities: any[];
  dynamicReportOrganizations: string[];
  limits: any;
  name: string;
  platforms: any;
  weatherConditions: any[];
  weatherFields: any[];
  workConditions: any[];
  widgetConfig: any[];
  dailyReportLocationConfig: any;
  translations: any;
  offsetTypes: any[];
  searchResultsInfo: any;
  exprEvalConfig: any;
}

const initialState: SharedState = {
  loading: [],
  appDataDetails: null,
  abilities: [],
  dynamicReportOrganizations: [],
  limits: null,
  name: '',
  platforms: null,
  weatherConditions: [],
  weatherFields: [],
  workConditions: [],
  widgetConfig: [],
  dailyReportLocationConfig: null,
  translations: {},
  offsetTypes: [],
  searchResultsInfo: {
    searchResults: [],
    // Generic per-entity map keyed by searchType (see shared/constants/search-entities.ts).
    resultsByType: {},
    // Legacy keys kept for existing consumers.
    dailyReports: [],
    issues: [],
    loading: false,
  },
  exprEvalConfig: {},
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, () => ({
    ...initialState,
  })),

  // START LOADING
  on(StartLoading, state => ({
    ...state,
    loading: [...state.loading, 1],
  })),

  // STOP LOADING
  on(StopLoading, state => ({
    ...state,
    loading: [...state.loading].slice(1),
  })),

  // CLEAR LOADING
  on(ClearLoading, state => ({
    ...state,
    loading: [],
  })),

  // FETCH APP Data
  on(FetchAppDataRequest, state => ({
    ...state,
    loading: [...state.loading, 1],
    appDataDetails: null,
    abilities: [],
    dynamicReportOrganizations: [],
    limits: null,
    name: '',
    platforms: null,
    weatherConditions: [],
    weatherFields: [],
    workConditions: [],
    widgetConfig: [],
    dailyReportLocationConfig: null,
    offsetTypes: [],
    exprEvalConfig: {},
  })),

  on(FetchAppDataSuccess, (state, props) => ({
    ...state,
    loading: [...state.loading].slice(1),
    appDataDetails: props.data || null,
    abilities: props.data.abilities || [],
    dynamicReportOrganizations: props.data.dynamicReportOrganizations || [],
    limits: props.data.limits || null,
    name: props.data.name || '',
    platforms: props.data.platforms || null,
    weatherConditions: props.data.weatherConditions || [],
    workConditions: props.data.workConditions || [],
    weatherFields: props.data.weatherFields || [],
    widgetConfig: props.data.widgetConfig || [],
    dailyReportLocationConfig: props.data.dailyReportLocationConfig || null,
    offsetTypes: props.data.offsetTypes || [],
    exprEvalConfig: props.data.exprEvalConfig || {},
  })),

  on(FetchAppDataFailure, state => ({
    ...state,
    loading: [...state.loading].slice(1),
    appDataDetails: null,
    abilities: [],
    dynamicReportOrganizations: [],
    limits: null,
    name: '',
    platforms: null,
    weatherConditions: [],
    weatherFields: [],
    workConditions: [],
    widgetConfig: [],
    dailyReportLocationConfig: null,
    exprEvalConfig: {},
  })),

  // FETCH SEARCH DETAILS
  on(FetchSearchDetailsRequest, state => ({
    ...state,
    searchResultsInfo: {
      ...initialState.searchResultsInfo,
      searchResults: initialState.searchResultsInfo.searchResults,
      resultsByType: initialState.searchResultsInfo.resultsByType,
      dailyReports: initialState.searchResultsInfo.dailyReports,
      issues: initialState.searchResultsInfo.issues,
      loading: true,
    },
  })),

  on(FetchSearchDetailsSuccess, (state, props) => ({
    ...state,
    searchResultsInfo: {
      ...state.searchResultsInfo,
      searchResults: props.searchResults,
      resultsByType: props.resultsByType || {},
      totalRecords: props.totalRecords ?? props.searchResults?.length ?? 0,
      dailyReports: props.dailyReports,
      issues: props.issues,
      loading: false,
    },
  })),

  on(FetchSearchDetailsFailure, state => ({
    ...state,
    searchResultsInfo: {
      ...state.searchResultsInfo,
      searchResults: state.searchResultsInfo.searchResults,
      resultsByType: state.searchResultsInfo.resultsByType,
      dailyReports: state.searchResultsInfo.dailyReports,
      issues: state.searchResultsInfo.issues,
      loading: false,
    },
  })),

  // FETCH TRANSLATIONS
  on(FetchTranslationsRequest, (state) => ({
    ...state,
    translations: initialState.translations,
  })),
  on(FetchTranslationsSuccess, (state, props) => ({
    ...state,
    translations: props.translations,
  })),
  on(FetchTranslationsFailure, (state) => state),
);
